import { find, flow, isNil, isNull, join, keys, last, map, nth, reduce, size, sortBy, toLower } from 'lodash/fp';

import { Service, WaitingPeriod } from '../../types';

type ServiceGroup = {
  serviceItemNames: string[];
  waitingPeriod: WaitingPeriod;
};

const groupServicesByWaitingPeriod = (serviceGroups: ServiceGroup[], service: Service): ServiceGroup[] => {
  // @ts-expect-error - Automatic, Please fix when editing this file
  const waitingPeriod = service.waitingPeriod.standard;

  const existingGroupForWaitingPeriod = find({ waitingPeriod }, serviceGroups) as ServiceGroup;
  if (existingGroupForWaitingPeriod) {
    existingGroupForWaitingPeriod.serviceItemNames.push(toLower(service.name));
    return serviceGroups;
  }

  return [...serviceGroups, { serviceItemNames: [toLower(service.name)], waitingPeriod }];
};

const formattedDurations = { M: 'month', d: 'day', y: 'year' };
const durationRegexp = new RegExp(`(\\d+)(${keys(formattedDurations).join('|')})$`);

const formatWaitingPeriod = (duration: string) => {
  if (isNil(duration)) {
    return 'No';
  }

  const matches = duration.match(durationRegexp);
  if (isNull(matches)) {
    return duration;
  }

  const [, number, unit] = matches;
  // @ts-expect-error - Automatic, Please fix when editing this file
  return `${number} ${formattedDurations[unit]}`;
};

// this adds a separator to each item name - a comma and space between each, with an ampersand between the last two
const buildSeparatedServiceItemNames = (serviceItemNames: string[]): string =>
  flow(
    map((name) => {
      const isLastItem = name === last(serviceItemNames);
      if (isLastItem) {
        return name;
      }

      const isSecondLastItem = name === nth(-2, serviceItemNames) && size(serviceItemNames) > 1;
      if (isSecondLastItem) {
        return `${name} & `;
      }

      return `${name}, `;
    }),
    join(''),
  )(serviceItemNames);

const summariseWaitingPeriods = (services: Service[]): string[] => {
  let groupedServicesByWaitingPeriod = reduce(groupServicesByWaitingPeriod, [], services);
  groupedServicesByWaitingPeriod = sortBy((item) => size(item.serviceItemNames), groupedServicesByWaitingPeriod);

  const hasMoreThanOneGroup = size(groupedServicesByWaitingPeriod) > 1;
  const lastGroup = last(groupedServicesByWaitingPeriod);
  // @ts-expect-error - Automatic, Please fix when editing this file
  const lastGroupHasMoreThanOneService = size(lastGroup.serviceItemNames) > 1;

  const shouldSummariseLastGroup = hasMoreThanOneGroup && lastGroupHasMoreThanOneService;
  if (shouldSummariseLastGroup) {
    // @ts-expect-error - Automatic, Please fix when editing this file
    lastGroup.serviceItemNames = ['other included services'];
  }

  const shouldNotPrintServiceNames = !hasMoreThanOneGroup;
  if (shouldNotPrintServiceNames) {
    // @ts-expect-error - Automatic, Please fix when editing this file
    return [`${formatWaitingPeriod(lastGroup.waitingPeriod)} waiting period`];
  }

  return map((group) => {
    const serviceItemNames = buildSeparatedServiceItemNames(group.serviceItemNames);
    return `${formatWaitingPeriod(group.waitingPeriod)} waiting period for ${serviceItemNames}`;
  }, groupedServicesByWaitingPeriod);
};

export default summariseWaitingPeriods;
